/* Vínculo `cbid` ↔ cliente logado — fase 3 do B4 (spec 0121 do
 * `briotecnologia/123zap-site-automation`).
 *
 * O `123zap_cbid` é o identificador próprio de navegador da 123zap: cookie de
 * 400 dias emitido no edge do `www` com `Domain=.123zap.com.br`, que por isso
 * chega aqui sem nenhuma integração. Ele carrega a jornada anônima — campanha,
 * origem, primeiro toque. Amarrá-lo a uma CONTA é o que transforma esse
 * histórico em atribuição de cliente real.
 *
 * ## Por que no login também, e não só no cadastro
 *
 * Quem cadastra nem sempre é quem a campanha ativou. Um sócio abre a conta e
 * quem viu o anúncio entra dias depois, do próprio navegador, com o `cbid` que
 * carrega o histórico da campanha. É o caso das vendas que hoje aparecem
 * órfãs de atribuição: o toque existiu, num navegador que nunca preencheu
 * formulário. Capturar só no cadastro perde essa pessoa.
 *
 * ## Carimba, não consulta
 *
 * Envio fire-and-forget: nunca bloqueia a sessão, nunca lança, nunca espera
 * resposta. Não existe leitura por `cbid` no CDP — introduzir uma aqui criaria
 * um requisito de latência que hoje não existe.
 *
 * ## Indício, não autenticação
 *
 * O endpoint é público e o vínculo chega do cliente. Serve para atribuição;
 * nunca para autorizar acesso a nada.
 */

/* 🔴 É o `_js`, e não o `123zap_cbid`. O cookie canônico é **HttpOnly** —
 * `document.cookie` nunca o enxerga, e o painel é SPA em S3+CloudFront, sem
 * edge que injete o valor como o `www` faz.
 *
 * Isso já custou caro uma vez: o formulário lia o canônico e mandou 4 leads
 * sem `cbid` ao CDP com toda a cadeia de GTM correta, sem erro nenhum. O
 * espelho `123zap_cbid_js` é emitido pelo edge do `www` com o mesmo valor e
 * `Domain=.123zap.com.br` — ver `123zap.com.br` PR #13. */
const COOKIE = "123zap_cbid_js";
const UUIDV4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/* Quantos logins de cada conta carregam identidade.
 *
 * O valor de atribuição está na janela em que pessoas NOVAS ainda aparecem;
 * depois vira telemetria repetida de quem já conhecemos. É heurística de
 * custo, não de correção — se errar para mais, o `first-wins` do CDP protege
 * a atribuição. */
const MAX_LOGINS = 5;

const CONTADOR_KEY = "cbid.identidadeLogins";
const SESSAO_KEY = "cbid.identidadeEnviada";

function safeGet(store, key) {
  try {
    if (typeof window === "undefined") return null;
    return window[store].getItem(key);
  } catch {
    return null;
  }
}

function safeSet(store, key, value) {
  try {
    if (typeof window === "undefined") return;
    window[store].setItem(key, value);
  } catch {
    // Ignora armazenamento bloqueado — telemetria não é motivo para quebrar.
  }
}

export function readCbid() {
  if (typeof document === "undefined") return null;

  const bruto = document.cookie
    .split(";")
    .map((parte) => parte.trim())
    .find((parte) => parte.startsWith(`${COOKIE}=`));

  if (!bruto) return null;

  const valor = decodeURIComponent(bruto.slice(COOKIE.length + 1));

  /* Só UUIDv4 vira chave de partição no CDP — o que não casa seria 422 do
   * outro lado. Barrar aqui evita ruído no endpoint e no gráfico de erro. */
  return UUIDV4.test(valor) ? valor : null;
}

/* `CREATE_USER` é gravado pelo `useSignupForm` logo antes de redirecionar para
 * a raiz. Distinguir os dois momentos não é cosmético: no cadastro o vínculo é
 * o da conversão; no login pode ser a pessoa diferente. */
function momentoDaSessao(methodLogin) {
  return methodLogin === "CREATE_USER" ? "cadastro" : "login";
}

/**
 * Envia o vínculo `cbid` ↔ conta, uma vez por conta por sessão de aba.
 *
 * Devolve o payload enviado (útil em teste) ou `null` quando não houve envio.
 * Nunca lança e nunca rejeita — quem chama não precisa de `try`/`catch`.
 */
export function sendCbidIdentity({ accountId, email, methodLogin } = {}) {
  const url = process.env.REACT_APP_CBID_INGEST_URL;
  const token = process.env.REACT_APP_CBID_INGEST_TOKEN;

  /* Inerte sem as variáveis: o painel sobe sem elas e o envio liga sem novo
   * deploy de código, igual à borda do `www`. */
  if (!url || !token || !accountId) return null;

  const cbid = readCbid();
  if (!cbid) return null;

  /* A guarda é POR CONTA, não por aba: a relação pessoa↔conta é
   * muitos-para-muitos e trocar de conta na mesma aba é um vínculo novo, que
   * é justamente o que queremos enxergar. */
  const sessaoKey = `${SESSAO_KEY}:${accountId}`;
  if (safeGet("sessionStorage", sessaoKey) === "1") return null;

  const momento = momentoDaSessao(methodLogin);
  const contadorKey = `${CONTADOR_KEY}:${accountId}`;
  const jaEnviados = Number(safeGet("localStorage", contadorKey) || 0);

  if (momento === "login" && jaEnviados >= MAX_LOGINS) return null;

  const identidade = { account_id: accountId, momento };
  if (email) identidade.email = email;
  if (momento === "login") identidade.login_n = jaEnviados + 1;

  const payload = {
    cbid,
    ts: new Date().toISOString(),
    tipo: "identidade",
    identidade,
  };

  safeSet("sessionStorage", sessaoKey, "1");

  /* O contador conta LOGINS. Se o cadastro consumisse uma vaga, o primeiro
   * login da conta chegaria ao CDP como `login_n: 2` — número errado no dado,
   * e é do dado que a análise vai depender. */
  if (momento === "login") {
    safeSet("localStorage", contadorKey, String(jaEnviados + 1));
  }

  try {
    /* `keepalive` para o envio sobreviver a uma navegação imediata. O
     * `catch` vazio é deliberado: falha de telemetria de atribuição não pode
     * virar erro visível para o cliente. */
    window
      .fetch(url, {
        method: "POST",
        keepalive: true,
        headers: {
          "content-type": "application/json",
          "x-verify-token": token,
        },
        body: JSON.stringify(payload),
      })
      .catch(() => {});
  } catch {
    // fetch indisponível (ambiente de teste, navegador antigo) — segue a vida.
  }

  return payload;
}
